import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { PriceListSummary } from '../services/api';
import { Loader } from './Loader';
import { Button } from './Button';
import { Input } from './Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './Dialog';
import { Plus, PencilSimple, Trash, MagnifyingGlass } from 'phosphor-react';

export interface PriceListTableHandle {
  reload: () => void;
}

interface PriceListTableProps {
  onSelectList: (id: number) => void;
  onSaved?: () => void;
}

export const PriceListTable = forwardRef<PriceListTableHandle, PriceListTableProps>(
  ({ onSelectList, onSaved }, ref) => {
    const api = useApi();
    const [lists, setLists] = useState<PriceListSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    // Create/Edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [nameInput, setNameInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [fieldError, setFieldError] = useState<string | null>(null);

    // Delete dialog
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const { lists: data } = await api.getPriceLists();
        setLists(data);
      } catch (e) {
        setError('No se pudo cargar las listas de precios.');
      } finally {
        setLoading(false);
      }
    }, [api]);

    useImperativeHandle(ref, () => ({ reload: load }));

    useEffect(() => { load(); }, [load]);

    const formatDate = (dateStr: string) => {
      try {
        return new Date(dateStr).toLocaleDateString('es-CL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      } catch {
        return dateStr;
      }
    };

    const openCreate = () => {
      setEditingId(null);
      setNameInput('');
      setFieldError(null);
      setDialogOpen(true);
    };

    const openEdit = (pl: PriceListSummary) => {
      setEditingId(pl.id);
      setNameInput(pl.name);
      setFieldError(null);
      setDialogOpen(true);
    };

    const handleSave = async () => {
      if (!nameInput.trim()) { setFieldError('El nombre es requerido.'); return; }
      try {
        setSaving(true);
        setFieldError(null);
        if (editingId !== null) {
          const updated = await api.updatePriceList(editingId, nameInput.trim());
          setLists(prev => prev.map(pl => pl.id === editingId ? { ...pl, name: updated.name } : pl));
        } else {
          const created = await api.createPriceList(nameInput.trim());
          setLists(prev => [...prev, { ...created, itemCount: 0, clientCount: 0, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }]);
        }
        setDialogOpen(false);
        onSaved?.();
      } catch (e) {
        setFieldError(e instanceof Error ? e.message : 'Error al guardar.');
      } finally {
        setSaving(false);
      }
    };

    const openDelete = (id: number) => {
      setDeletingId(id);
      setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
      if (deletingId === null) return;
      try {
        setDeleting(true);
        await api.deletePriceList(deletingId);
        setLists(prev => prev.filter(pl => pl.id !== deletingId));
        setDeleteDialogOpen(false);
        onSaved?.();
      } catch {
        // keep dialog open
      } finally {
        setDeleting(false);
      }
    };

    return (
      <div className="space-y-6">
        {/* Search + Create */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" />
            <input
              type="text"
              placeholder="Buscar lista..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm font-sans outline-none focus:border-primary transition-colors"
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={16} weight="bold" />}
            onClick={openCreate}
          >
            Crear lista
          </Button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-text-subtle">
            <Loader />
            <span className="font-sans text-sm">Cargando...</span>
          </div>
        )}

        {error && !loading && (
          <p className="font-sans text-sm text-destructive">{error}</p>
        )}

        {!loading && !error && (
          <div className="flex flex-col gap-2">
            {lists.filter(pl => pl.name.toLowerCase().includes(search.toLowerCase())).map((pl) => (
              <div
                key={pl.id}
                className="flex items-center justify-between px-4 py-3 rounded-lg border border-border bg-background hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => onSelectList(pl.id)}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-sans font-medium text-sm text-text truncate">{pl.name}</p>
                  <p className="font-sans text-xs text-text-subtle mt-0.5">
                    {pl.itemCount} producto{pl.itemCount !== 1 ? 's' : ''} &middot; {pl.clientCount} cliente{pl.clientCount !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <p className="font-sans text-xs text-text-subtle whitespace-nowrap">
                    {formatDate(pl.updated_at)}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openEdit(pl); }}
                    className="p-1.5 rounded hover:bg-muted text-text-subtle hover:text-text transition-colors"
                    aria-label="Renombrar lista"
                  >
                    <PencilSimple size={15} weight="regular" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openDelete(pl.id); }}
                    className="p-1.5 rounded hover:bg-destructive/10 text-text-subtle hover:text-destructive transition-colors"
                    aria-label="Eliminar lista"
                  >
                    <Trash size={15} weight="regular" />
                  </button>
                </div>
              </div>
            ))}

            {lists.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-border rounded-lg">
                <p className="font-sans text-sm text-text-subtle text-center">
                  No hay listas de precios cargadas a&uacute;n.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Create / Rename dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="p-6">
            <DialogHeader className="mb-4">
              <DialogTitle>{editingId !== null ? 'Renombrar lista' : 'Crear lista de precios'}</DialogTitle>
            </DialogHeader>
            <Input
              type="text"
              placeholder="Nombre de la lista"
              value={nameInput}
              onChange={(e) => { setNameInput(e.target.value); setFieldError(null); }}
              error={!!fieldError}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            />
            {fieldError && (
              <p className="font-sans text-sm text-destructive mt-1">{fieldError}</p>
            )}
            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? <Loader /> : editingId !== null ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="p-6">
            <DialogHeader className="mb-4">
              <DialogTitle>Eliminar lista de precios</DialogTitle>
            </DialogHeader>
            <p className="font-sans text-sm text-text">
              ¿Est&aacute;s seguro que deseas eliminar{' '}
              <strong>{lists.find(pl => pl.id === deletingId)?.name}</strong>?
              Se eliminar&aacute;n todos los items y asignaciones de clientes. Esta acci&oacute;n no se puede deshacer.
            </p>
            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting ? <Loader /> : 'Eliminar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);
